/* eslint-disable react/require-default-props */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { ReactElement } from 'react';

import { Form } from '@unform/web';
import axios from 'axios';
import CurrencyInput from 'react-currency-input-field';
import { IoCloseSharp } from 'react-icons/io5';
import { toast } from 'react-toastify';

import { ModalInfo, Button, Input, Select } from '~/components';
import { useLoading } from '~/hooks/loading';
import { IUserContract } from '~/models/Contracts';
import { IProfessionalForm, IUser } from '~/models/User';
import { CollaboratorsService, ProfessionalService } from '~/services';
import { ICollaboratorEdit } from '~/services/Collaborators';
import ContractsService from '~/services/Contracts';
import { getCEP, removeMark } from '~/utils';
import formatDateISO from '~/utils/formatDateIso';
import parseDateFromISO from '~/utils/parseDateFromISO';
import { statusOptions } from '~/utils/status';

import * as S from './styles';

interface ICollaboratorModalProps {
  modalIsVisible: boolean;
  trainerId: number;
  contractId: number;
  setModalIsVisible: Dispatch<SetStateAction<boolean>>;
  editForm: boolean;
  blockedEdit?: boolean;
}

export interface ICollaboratorChange {
  public_name: string;
  name: string;
  cpf_cnpj: string;
  professional_register: string;
  date_of_birth: string;
  email: string;
  phone: string;
  cep: string;
  address: string;
  number: string;
  complement?: string;
  city: string;
  UF: string;
  country: string;
  role: string;
}

const modalityPaymentOptions = [
  { label: 'À vista', value: 'cash_payment' },
  { label: 'Parcelado', value: 'installments' },
];

const methodPaymentOptions = [
  { label: 'Cartão de Crédito', value: 'credit_card' },
  { label: 'Cartão de Debito', value: 'debit_card' },
  { label: 'Boleto', value: 'billet' },
  { label: 'Dinheiro', value: 'cash' },
  { label: 'PIX', value: 'pix' },
  { label: 'Cheque', value: 'check' },
];

function ContractModal({
  editForm,
  modalIsVisible,
  trainerId,
  contractId,
  setModalIsVisible,
  blockedEdit = false,
}: ICollaboratorModalProps): ReactElement {
  const { setLoading } = useLoading();
  const [user, setUser] = useState<IUser>();
  const [isRemovingCollaborator, setIsRemovingCollaborator] = useState(false);
  const [collaboratorRole, setCollaboratorRole] = useState<ICollaboratorEdit>();
  const [contractInfo, setContractInfo] = useState<IUserContract>();
  const [trainer, setTrainer] = useState({
    role: 'professional',
    client_name: '',
    cpf_cnpj: '',
    activation_date: '',
    contract_months: '',
    contract_start: '',
    contract_end: '',
    contract_value: '0',
    payment_modality: 'installments',
    parcel_quantity: '',
    parcel_value: '0',
    payment_method: 'credit_card',
    status: '',
  });

  const handleGetUser = useCallback(async () => {
    try {
      setLoading(true);

      // const [fetchedUser] = await Promise.all([
      //   ProfessionalService.getOneUser(String(trainerId)),
      // ]);

      const { data } = await ProfessionalService.getOneUser(String(trainerId));
      if (data) {
        setTrainer((prevState) => ({
          ...prevState,
          client_name: data.name ? data.name : '',
          cpf_cnpj: data.professional?.cpf ? data.professional?.cpf : '',
        }));
        setUser(data);
      }
    } catch (error) {
      toast.error('Ocorreu um problema ao carregar os dados!');
    } finally {
      setLoading(false);
    }
  }, [trainerId, setLoading]);

  // const handleRemoveCollaborator = useCallback(async () => {
  //   try {
  //     setLoading(true);

  //     setIsRemovingCollaborator(true);
  //     const response = await CollaboratorsService.removeCollaborator(
  //       userTrainerId,
  //     );

  //     setLoading(false);
  //     if (response) toast.success('Colaborador removido com sucesso!');
  //   } catch (err) {
  //     console.log(err);
  //     toast.error('Houve um erro ao remover o colaborador');
  //     setLoading(false);
  //   } finally {
  //     setIsRemovingCollaborator(false);
  //     setModalIsVisible(false);
  //     setLoading(false);
  //   }
  // }, [userTrainerId]);

  const handleGetContractInfo = useCallback(async () => {
    if (contractId === -1) return;
    try {
      setLoading(true);

      const { data } = await ContractsService.getOneContract(
        String(contractId),
      );

      if (data) {
        setTrainer((prevState) => ({
          ...prevState,
          activation_date: formatDateISO(data[0].activation_date, 'invert'),
          contract_months: String(data[0].period_contract_month),
          contract_start: formatDateISO(
            data[0].period_contract_initial,
            'invert',
          ),
          contract_end: formatDateISO(data[0].period_contract_final, 'invert'),
          contract_value: data[0].contract_value,
          payment_modality: data[0].payment_modality,
          payment_method: data[0].payment_method,
          parcel_quantity: String(data[0].quantity_installment),
          parcel_value: data[0].installment_value,
          status: data[0].status,
        }));
        setContractInfo(data[0]);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [contractId, setLoading]);

  const handleGetCollaborator = useCallback(async () => {
    if (trainerId === -1) return;
    try {
      setLoading(true);

      const { data } = await CollaboratorsService.getOnlyCollaborator(
        trainerId,
      );

      if (data) setCollaboratorRole(data);

      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  }, [trainerId]);

  const closeModal = useCallback(() => {
    setModalIsVisible(!modalIsVisible);
    // setTrainer({
    //   role: 'professional',
    //   client_name: '',
    //   cpf_cnpj: '',
    //   activation_date: '',
    //   contract_months: '',
    //   contract_start: '',
    //   contract_end: '',
    //   contract_value: '0',
    //   payment_modality: 'installments',
    //   parcel_quantity: '',
    //   parcel_value: '0',
    //   payment_method: 'credit_card',
    //   status: '',
    // });
  }, [modalIsVisible, setModalIsVisible]);

  useEffect(() => {
    handleGetCollaborator();
    handleGetUser();
    handleGetContractInfo();
  }, [trainerId, handleGetCollaborator, handleGetUser, handleGetContractInfo]);

  const handleSubmit = useCallback(async () => {
    if (blockedEdit) {
      toast.error(
        'Edição de contrato bloqueada. Para editar um contrato será necessário atualizar o status',
      );
      return;
    }
    if (
      !trainer ||
      !trainer.client_name ||
      !trainer.cpf_cnpj ||
      !trainer.activation_date ||
      !trainer.contract_months ||
      !trainer.contract_start ||
      !trainer.contract_end ||
      !trainer.contract_value ||
      !trainer.payment_method ||
      !trainer.payment_modality
    ) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    if (
      trainer.payment_modality === 'installments' &&
      !trainer.parcel_quantity &&
      !trainer.parcel_value
    ) {
      toast.error('Preencha a quantidade de parcelas e o valor da parcela');
      return;
    }
    const activationDate = parseDateFromISO(trainer.activation_date);

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);

    const contractEndDate = parseDateFromISO(trainer.contract_end);

    const contractStartDate = parseDateFromISO(trainer.contract_start);

    // if (activationDate > currentDate) {
    //   toast.error('Data de ativação inválida. Não pode ser no futuro.');
    //   return;
    // }

    if (contractStartDate > contractEndDate) {
      toast.error(
        'A Data do período inicial não pode ser maior que do período final.',
      );
      // eslint-disable-next-line no-useless-return
      return;
    }
    try {
      const createContractData: any = {
        activation_date: trainer.activation_date,
        period_contract_month: trainer.contract_months,
        period_contract_initial: trainer.contract_start,
        period_contract_final: trainer.contract_end,
        contract_value: trainer.contract_value,
        payment_modality: trainer.payment_modality,
        payment_method: trainer.payment_method,
        status: trainer.status,
      };

      if (trainer.payment_modality === 'installments') {
        createContractData.quantity_installment = trainer.parcel_quantity;
        createContractData.installment_value = trainer.parcel_value;
      }

      const { data } = await ContractsService.validateContractActive(
        user?.professional?.id ? String(user?.professional?.id) : '',
      );

      if (
        data.message === 'Active Contract' &&
        trainer.status === 'active' &&
        !editForm
      ) {
        toast.error(
          'Já existe um contrato ativo para este profissional. Não é possível criar um novo contrato enquanto o anterior estiver ativo.',
        );
        return;
      }

      const editContractData: any = {
        activation_date: trainer.activation_date,
        period_contract_month: trainer.contract_months,
        period_contract_initial: trainer.contract_start,
        period_contract_final: trainer.contract_end,
        contract_value: trainer.contract_value,
        payment_modality: trainer.payment_modality,
        payment_method: trainer.payment_method,
      };

      if (trainer.payment_modality === 'installments') {
        editContractData.quantity_installment = trainer.parcel_quantity;
        editContractData.installment_value = trainer.parcel_value;
      }
      if (
        !(
          data.contract_number === contractInfo?.contract_number &&
          data.message === 'Active Contract' &&
          editForm
        )
      ) {
        editContractData.status = trainer.status;
      }

      setLoading(true);
      if (editForm) {
        const response = await ContractsService.editContract(
          String(contractId),
          editContractData,
        );
        if (response) {
          toast.success('Contrato editado com sucesso!');
          closeModal();
          // location.reload();
        }
      } else {
        const response = await ContractsService.createContract(
          user?.professional?.id ? String(user?.professional?.id) : '',
          createContractData,
        );
        if (response) {
          toast.success('Contrato criado com sucesso!');
          closeModal();
        }
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        if (err.response?.data.message) {
          toast.error(err.response?.data.message);
        } else if (err.response?.data) {
          toast.error(err.response?.data);
        }
      } else {
        toast.error('Ocorreu um erro');
      }
    } finally {
      setLoading(false);
    }
  }, [
    blockedEdit,
    trainer,
    user?.professional?.id,
    editForm,
    contractInfo?.contract_number,
    setLoading,
    contractId,
    closeModal,
  ]);

  useEffect(() => {
    if (trainer.contract_value && trainer.parcel_quantity) {
      setTrainer((prevState) => {
        return {
          ...prevState,
          parcel_value: (
            parseFloat(prevState.contract_value) /
            parseFloat(prevState.parcel_quantity)
          ).toFixed(2),
        };
      });
    }
  }, [trainer.contract_value, trainer.parcel_quantity]);
  // useEffect(() => {
  //   if (collaboratorRole && editForm) {
  //     setCollaborator({
  //       email: collaboratorRole.collaborator.user.email,
  //       modules: collaboratorRole.collaborator.modules,
  //       name: collaboratorRole.collaborator.user.name,
  //       observations: collaboratorRole.collaborator.observations,
  //       password: collaboratorRole.dec_password,
  //     });
  //   } else {
  //     setCollaborator({
  //       name: '',
  //       email: '',
  //       password: '',
  //       modules: [],
  //       observations: '',
  //     });
  //   }
  // }, [collaboratorRole, editForm]);

  return (
    <ModalInfo
      modalVisible={modalIsVisible}
      setModalVisible={setModalIsVisible}
      containerStyle={{
        justifyContent: 'flex-start',
        padding: '3rem',
        alignItems: 'flex-start',
        maxWidth: '75rem',
      }}
    >
      <S.ConfirmDeleteContainer>
        <S.HeaderContainer>
          <S.ConfirmDeleteTitle>
            {editForm
              ? `Dados de contrato - #${contractInfo?.contract_number}`
              : 'Dados de contrato'}
          </S.ConfirmDeleteTitle>
          <IoCloseSharp
            style={{ cursor: 'pointer' }}
            size={30}
            onClick={() => closeModal()}
          />
        </S.HeaderContainer>
        <S.Divider
          style={{
            borderBottom: '1px solid #A6A6A6',
            width: 'calc(100% + 30px)',
          }}
        />
        <S.FormWrapper
          onSubmit={() => {
            console.log('contract');
          }}
        >
          <S.Body>
            <S.Side>
              <Input
                name="trainer_client_name"
                label="Nome do Cliente"
                placeholder="Nome"
                disabled
                value={trainer.client_name}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, client_name: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <S.EditUserInputBlock>
                <p>CPF/CNPJ</p>
                <Input
                  name="trainer_cpf_cnpj"
                  disabled
                  value={trainer.cpf_cnpj}
                  maxLength={14}
                  placeholder="000.000.000-00"
                  onChange={({ target }) => {
                    setTrainer((prevState) => {
                      return {
                        ...prevState,
                        cpf_cnpj: removeMark(target.value),
                      };
                    });
                  }}
                />
              </S.EditUserInputBlock>
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <Input
                name="trainer_activation_date"
                value={trainer.activation_date}
                disabled={blockedEdit}
                label="Data de Ativação"
                placeholder="00/00/0000"
                type="date"
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, activation_date: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <Input
                name="trainer_contract_months"
                label="Período de Contrato em Meses"
                disabled={blockedEdit}
                value={trainer.contract_months}
                placeholder="Número de meses"
                type="number"
                min={1}
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return {
                      ...prevState,
                      contract_months: target.value,
                    };
                  })
                }
              />
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <Input
                name="trainer_contract_start"
                value={trainer.contract_start}
                label="Período de Contrato - Início"
                disabled={blockedEdit}
                placeholder="00/00/0000"
                type="date"
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, contract_start: target.value };
                  })
                }
              />
            </S.Side>
            <S.Side>
              <Input
                name="trainer_contract_end"
                value={trainer.contract_end}
                label="Período de Contrato - Fim"
                disabled={blockedEdit}
                placeholder="00/00/0000"
                type="date"
                onChange={({ target }) =>
                  setTrainer((prevState) => {
                    return { ...prevState, contract_end: target.value };
                  })
                }
              />
            </S.Side>
          </S.Body>
          <S.Body>
            <S.Side>
              <S.EditUserInputBlock>
                <p>Valor de Contrato</p>
                <CurrencyInput
                  id="input-currency"
                  name="contract_value"
                  value={trainer.contract_value ? trainer.contract_value : 0}
                  disabled={blockedEdit}
                  decimalsLimit={2}
                  style={{
                    backgroundColor: blockedEdit ? '#E0E0E0' : '#ffffff',
                  }}
                  prefix="R$ "
                  decimalSeparator=","
                  groupSeparator="."
                  onValueChange={(value) => {
                    if (value) {
                      setTrainer((prevState) => {
                        return {
                          ...prevState,
                          contract_value: value,
                        };
                      });
                    }
                  }}
                />
              </S.EditUserInputBlock>
            </S.Side>
            <S.Side>
              <S.EditUserInputBlock>
                <p>Modalidade de pagamento</p>
                <Select
                  style={{
                    textAlign: 'left',
                    padding: '0 8px',
                    fontSize: '13px',
                    color: '#000000',
                  }}
                  containerStyle={{ marginBottom: '0' }}
                  name="modality_payment"
                  isDisabled={blockedEdit}
                  value={trainer.payment_modality}
                  options={modalityPaymentOptions}
                  onChange={(event) => {
                    setTrainer((prevState) => {
                      return {
                        ...prevState,
                        payment_modality: event.target.value,
                      };
                    });
                  }}
                />
              </S.EditUserInputBlock>
            </S.Side>
          </S.Body>
          {trainer.payment_modality !== 'cash_payment' && (
            <S.Body>
              <S.Side>
                <Input
                  name="trainer_parcel_quantity"
                  label="Quantidade de parcelas"
                  disabled={blockedEdit}
                  value={trainer.parcel_quantity}
                  placeholder="Quantidade de parcelas"
                  type="number"
                  min={0}
                  onChange={({ target }) =>
                    setTrainer((prevState) => {
                      return {
                        ...prevState,
                        parcel_quantity: target.value,
                      };
                    })
                  }
                />
              </S.Side>
              <S.Side>
                <S.EditUserInputBlock>
                  <p>Valor da parcela</p>
                  <CurrencyInput
                    id="input-currency"
                    name="parcel_value"
                    disabled={blockedEdit}
                    value={trainer.parcel_value ? trainer.parcel_value : 0}
                    decimalsLimit={2}
                    style={{
                      backgroundColor: blockedEdit ? '#E0E0E0' : '#ffffff',
                    }}
                    prefix="R$ "
                    onValueChange={(value) => {
                      if (value) {
                        setTrainer((prevState) => {
                          return {
                            ...prevState,
                            parcel_value: value,
                          };
                        });
                      }
                    }}
                  />
                </S.EditUserInputBlock>
              </S.Side>
            </S.Body>
          )}
          <S.Body>
            <S.Side style={{ width: '49%' }}>
              <S.EditUserInputBlock>
                <p>Método de pagamento</p>
                <Select
                  style={{
                    textAlign: 'left',
                    padding: '0 8px',
                    fontSize: '13px',
                    color: '#000000',
                  }}
                  containerStyle={{ marginBottom: '0' }}
                  name="payment_method"
                  isDisabled={blockedEdit}
                  value={trainer.payment_method}
                  options={methodPaymentOptions}
                  onChange={(event) => {
                    setTrainer((prevState) => {
                      return {
                        ...prevState,
                        payment_method: event.target.value,
                      };
                    });
                  }}
                />
              </S.EditUserInputBlock>
            </S.Side>
            <S.Side style={{ width: '49%' }}>
              <S.EditUserInputBlock>
                <p>Status</p>
                <Select
                  style={{
                    textAlign: 'left',
                    padding: '0 8px',
                    fontSize: '13px',
                    color: '#000000',
                  }}
                  containerStyle={{ marginBottom: '0' }}
                  name="status"
                  isDisabled={blockedEdit}
                  value={trainer.status}
                  options={statusOptions}
                  onChange={(event) => {
                    setTrainer((prevState) => {
                      return {
                        ...prevState,
                        status: event.target.value,
                      };
                    });
                  }}
                />
              </S.EditUserInputBlock>
            </S.Side>
          </S.Body>
        </S.FormWrapper>
        <S.ConfirmDeleteButtonGroup>
          {!editForm && (
            <Button
              size="small"
              textContent="Cancelar"
              onClick={() => closeModal()}
              variant="red"
              extraStyles={{
                maxWidth: '200px',
                maxHeight: 35,
              }}
            />
          )}
          {!blockedEdit && (
            <Button
              size="small"
              textContent={editForm ? 'Salvar' : 'Cadastrar'}
              onClick={handleSubmit}
              variant="black"
              extraStyles={{
                maxWidth: '200px',
                maxHeight: 35,
                marginLeft: 17,
              }}
            />
          )}
        </S.ConfirmDeleteButtonGroup>
      </S.ConfirmDeleteContainer>
    </ModalInfo>
  );
}

export default ContractModal;
